import MapWithComboBox from "@/app/activity/components/mapWithComboBox/MapWithComboBox"
import { TabActivityInfoProps } from "@/app/activity/components/upsertActivityDialog/TabActivityInfo/type"
import { getActivityColor } from "@/app/activity/list/utils"
import { createActivityStore } from "@/app/activity/store"
import DatePicker from "@/components/form/datePicker/DatePicker"
import Selector from "@/components/form/selector/Selector"
import TextField from "@/components/form/textField/TextField"
import TimePicker from "@/components/form/timePicker/TimePicker"
import { Activity, ActivityType, ActivityTypeKey, MatchDayType } from "@/types/activity"
import { useSeasonWeekQuery } from "@/hooks/api/useSettingQuery"
import { Box } from "@mui/material"
import Icon from "@/components/icon/Icon"
import { colors } from "@/components/colors"
import { isBefore } from "date-fns"

import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import "./tabActivityInfo.scss"

export default function TabActivityInfo({
  setDurationErrMessage,
  setIsDurationInValid,
  isDurationInValid,
  durationErrMessage,
  titleErrorMsg,
  typeErrorMsg,
  matchDayErrorMsg,
  touched,
  setTouched,
  submitted,
}: TabActivityInfoProps) {
  const { t } = useTranslation()

  const { setTempActivity, tempActivity } = createActivityStore((state) => ({
    setActivity: state.setTempActivity,
    setTempActivity: state.setTempActivity,
    tempActivity: state.tempActivity,
  }))

  // Duration 4개 인풋(시작 날짜·시간, 종료 날짜·시간)을 개별 추적한다.
  // 스토어의 dateStart/dateFinish는 날짜·시간이 모두 입력됐을 때만 채워진다.
  const [startDatePart, setStartDatePart] = useState<Date | null>(null)
  const [startTimePart, setStartTimePart] = useState<Date | null>(null)
  const [finishDatePart, setFinishDatePart] = useState<Date | null>(null)
  const [finishTimePart, setFinishTimePart] = useState<Date | null>(null)

  const isValidDate = (date: Date | null | undefined): date is Date =>
    !!date && !isNaN(date.getTime())

  const combineDateTime = (datePart: Date | null, timePart: Date | null) => {
    if (!isValidDate(datePart) || !isValidDate(timePart)) {
      return null
    }

    const merged = new Date(datePart)
    merged.setHours(timePart.getHours(), timePart.getMinutes(), timePart.getSeconds(), 0)

    return merged.toISOString()
  }

  const updateStart = (datePart: Date | null, timePart: Date | null) => {
    setStartDatePart(datePart)
    setStartTimePart(timePart)
    setTempActivity({ ...tempActivity, dateStart: combineDateTime(datePart, timePart) } as Activity)
  }

  const updateFinish = (datePart: Date | null, timePart: Date | null) => {
    setFinishDatePart(datePart)
    setFinishTimePart(timePart)
    setTempActivity({
      ...tempActivity,
      dateFinish: combineDateTime(datePart, timePart),
    } as Activity)
  }

  // 수정 모드·업로드 데이터 프리필처럼 스토어에 값이 먼저 채워지는 경우 인풋 상태를 동기화한다
  useEffect(() => {
    const dateStart = tempActivity?.dateStart ? new Date(tempActivity.dateStart) : null

    if (isValidDate(dateStart) && !startDatePart && !startTimePart) {
      setStartDatePart(dateStart)
      setStartTimePart(dateStart)
    }
  }, [tempActivity?.dateStart])

  useEffect(() => {
    const dateFinish = tempActivity?.dateFinish ? new Date(tempActivity.dateFinish) : null

    if (isValidDate(dateFinish) && !finishDatePart && !finishTimePart) {
      setFinishDatePart(dateFinish)
      setFinishTimePart(dateFinish)
    }
  }, [tempActivity?.dateFinish])

  const { data: seasonWeekData } = useSeasonWeekQuery()

  const activityType = useMemo(() => {
    return Object.entries(ActivityType).map(([key, value]) => ({ label: t(value), value: key }))
  }, [ActivityType])

  const seasonWeekOptions = useMemo(() => {
    const options: { label: string; value: number | null; startDate?: string; endDate?: string }[] =
      [{ label: "None", value: null }]
    if (!seasonWeekData?.seasonWeeks) return options
    return [
      ...options,
      ...seasonWeekData.seasonWeeks
        .filter((sw) => sw.active)
        .map((sw) => ({
          label: sw.name,
          value: sw.idfSeasonWeek as number | null,
          startDate: sw.startDate ?? "",
          endDate: sw.endDate ?? "",
        })),
    ]
  }, [seasonWeekData])

  useEffect(() => {
    if (!tempActivity?.dateStart || !tempActivity?.dateFinish) {
      setIsDurationInValid(true)
      setDurationErrMessage(t("rules.required") || "This field is required.")

      return
    }

    const dateStart = new Date(tempActivity.dateStart)
    const dateFinish = new Date(tempActivity.dateFinish)

    if (isNaN(dateStart.getTime()) || isNaN(dateFinish.getTime())) {
      setIsDurationInValid(true)
      setDurationErrMessage(t("rules.required") || "This field is required.")

      return
    }

    const isStartBeforeFinish = isBefore(dateStart, dateFinish)

    const isBefore8Hour = dateFinish.getTime() - dateStart.getTime() <= 8 * 60 * 60 * 1000

    // 백엔드(DateRange.ofDiffMore) 기준과 동일하게 정확히 1분(60초)은 허용한다
    const isAtLeast1Minute = dateFinish.getTime() - dateStart.getTime() >= 60 * 1000

    setDurationErrMessage(t(""))

    if (!isAtLeast1Minute) {
      // Localazy에 번역값이 비어 있는 동안에도 문구가 보이도록 폴백 처리
      setDurationErrMessage(
        t("messages.minimumActivityDuration") || "Activity duration must be at least 1 minute.",
      )
    }

    if (!isStartBeforeFinish) {
      setDurationErrMessage(t("rules.failTimeOrder"))
    }

    if (!isBefore8Hour) {
      setDurationErrMessage(t("messages.maximumActivityDuration"))
    }

    if (isStartBeforeFinish && isBefore8Hour && isAtLeast1Minute) {
      setDurationErrMessage("")
    }

    setIsDurationInValid(!isStartBeforeFinish || !isBefore8Hour || !isAtLeast1Minute)
  }, [tempActivity?.dateFinish, tempActivity?.dateStart])

  // 오류 문구·빨간 테두리는 Save를 누른 뒤에만 노출한다.
  // 미입력 시에는 비어 있는 인풋만, 기간 규칙 위반 시에는 4개 인풋 전체를 강조한다.
  // 입력이 채워지면 해당 인풋의 빨간 테두리는 즉시 해제된다.
  const isAllEntered = [startDatePart, startTimePart, finishDatePart, finishTimePart].every(
    isValidDate,
  )
  const showRuleError = isAllEntered && isDurationInValid
  const showStartDateError = submitted && (!isValidDate(startDatePart) || showRuleError)
  const showStartTimeError = submitted && (!isValidDate(startTimePart) || showRuleError)
  const showFinishDateError = submitted && (!isValidDate(finishDatePart) || showRuleError)
  const showFinishTimeError = submitted && (!isValidDate(finishTimePart) || showRuleError)
  const showDurationErrMessage = submitted && isDurationInValid

  return (
    <div className="UpsertActivityDialogCont">
      <TextField
        autoFocus
        defaultValue={tempActivity?.name}
        label={t("name")}
        onChange={(e) => setTempActivity({ ...tempActivity, name: e.target.value } as Activity)}
        onBlur={(e) => {
          setTouched((prev) => ({ ...prev, name: true }))
          if (!e.target.value || e.target.value.trim() === "") {
            setTempActivity({ ...tempActivity, name: null } as Activity)
          }
        }}
        placeholder={t("activityName")}
        error={(touched.name || submitted) && !!titleErrorMsg}
        showMessage={(touched.name || submitted) && !!titleErrorMsg}
        message={titleErrorMsg}
      />
      <div className="activity-type-match-day">
        <Selector
          label={t("type")}
          options={activityType}
          value={
            tempActivity?.type
              ? t(ActivityType[tempActivity.type as keyof typeof ActivityType])
              : null
          }
          fullWidth
          placeholder={t("select")}
          prependIcon={
            tempActivity?.type ? (
              <svg width="8" height="9" viewBox="0 0 8 9" fill="none">
                <circle cx="4" cy="4.5" r="4" fill={getActivityColor(tempActivity?.type || "")} />
              </svg>
            ) : undefined
          }
          renderOption={({ key, ...props }, option) => {
            return (
              <Box component="li" key={key} {...props}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="8"
                  height="9"
                  viewBox="0 0 8 9"
                  fill="none">
                  <circle cx="4" cy="4.5" r="4" fill={getActivityColor(option?.value || "")} />
                </svg>
                <span>{option?.label}</span>
              </Box>
            )
          }}
          error={(touched.type || submitted) && !!typeErrorMsg ? true : undefined}
          showMessage={(touched.type || submitted) && !!typeErrorMsg}
          message={typeErrorMsg}
          slotProps={{
            popper: {
              className: "activity-type-popper",
            },
          }}
          onBlur={() => {
            setTouched((prev) => ({ ...prev, type: true }))
            if (!tempActivity?.type) {
              setTempActivity({ ...tempActivity, type: null } as Activity)
            }
          }}
          onChange={(_: unknown, value: { label: string; value: string } | null) => {
            setTempActivity({ ...tempActivity, type: value?.value } as Activity)
          }}
        />
        <Selector
          label={t("season")}
          options={seasonWeekOptions}
          value={
            seasonWeekOptions.find((opt) => opt.value === (tempActivity?.idfSeasonWeek ?? null)) ??
            null
          }
          fullWidth
          placeholder={t("select")}
          isOptionEqualToValue={(
            option: { label: string; value: number | null },
            val: { label: string; value: number | null },
          ) => option.value === val.value}
          slotProps={{
            popper: {
              className: "activity-season-popper",
            },
          }}
          renderOption={(
            { key, ...props },
            option: { label: string; value: number | null; startDate?: string; endDate?: string },
          ) => {
            const isSelected = option.value === (tempActivity?.idfSeasonWeek ?? null)
            return (
              <Box
                component="li"
                key={key}
                {...props}
                className={`${props.className || ""} activity-season-option`}>
                <div className="activity-season-option-content">
                  <span className="activity-season-option-name">{option.label}</span>
                  {option.startDate && option.endDate && (
                    <span className="activity-season-option-date">
                      {option.startDate} ~ {option.endDate}
                    </span>
                  )}
                </div>
                {isSelected && <Icon type="check" size={16} color={colors.platinum[900]} />}
              </Box>
            )
          }}
          onChange={(_: unknown, value: { label: string; value: number | null } | null) =>
            setTempActivity({ ...tempActivity, idfSeasonWeek: value?.value ?? null } as Activity)
          }
        />
        <Selector
          label={t("matchDay")}
          options={Object.values(MatchDayType)}
          value={tempActivity?.matchDay || null}
          fullWidth
          placeholder={t("select")}
          onChange={(_: unknown, value: string | null) =>
            setTempActivity({ ...tempActivity, matchDay: value } as Activity)
          }
          onBlur={() => {
            setTouched((prev) => ({ ...prev, matchDay: true }))
            setTempActivity({
              ...tempActivity,
              matchDay: tempActivity?.matchDay || null,
            } as Activity)
          }}
          error={(touched.matchDay || submitted) && !!matchDayErrorMsg ? true : undefined}
          showMessage={(touched.matchDay || submitted) && !!matchDayErrorMsg}
          message={matchDayErrorMsg}
          slotProps={{
            popper: {
              className: "activity-match-day-popper",
            },
          }}
          renderOption={({ key, ...props }, type) => {
            const isSelected = type === tempActivity?.matchDay
            return (
              <Box component="li" key={key} className="activity-selector-item" {...props}>
                <span>{t(type as string)}</span>
                {isSelected && <Icon type="check" size={16} color={colors.platinum[900]} />}
              </Box>
            )
          }}
        />
      </div>
      <div className="activity-duration">
        <p>{t("duration")}</p>
        <div className="activity-duration-time">
          <div>
            <DatePicker
              error={showStartDateError}
              defaultValueNull
              placeholder="YYYY-MM-DD"
              value={startDatePart}
              slotProps={{
                textField: {
                  error: showStartDateError,
                  fullWidth: true,
                },
                popper: {
                  style: { zIndex: 1302 },
                },
              }}
              onChange={(value) => {
                updateStart(value ?? null, startTimePart)
              }}></DatePicker>
            <TimePicker
              error={showStartTimeError}
              slotProps={{
                textField: {
                  error: showStartTimeError,
                  readOnly: false,
                  fullWidth: true,
                },
                popper: {
                  style: { zIndex: 1302 },
                },
              }}
              value={startTimePart}
              onChange={(value) => {
                updateStart(startDatePart, value ?? null)
              }}
              onApply={(value) => {
                updateStart(startDatePart, value ?? null)
              }}
              onClear={() => {
                updateStart(startDatePart, null)
              }}></TimePicker>
          </div>
          <p>~</p>
          <div>
            <DatePicker
              error={showFinishDateError}
              defaultValueNull
              placeholder="YYYY-MM-DD"
              value={finishDatePart}
              slotProps={{
                textField: {
                  error: showFinishDateError,
                  fullWidth: true,
                },
                popper: {
                  style: { zIndex: 1302 },
                },
              }}
              onChange={(value) => {
                updateFinish(value ?? null, finishTimePart)
              }}></DatePicker>
            <TimePicker
              error={showFinishTimeError}
              slotProps={{
                textField: {
                  error: showFinishTimeError,
                  readOnly: false,
                  fullWidth: true,
                },
                popper: {
                  style: { zIndex: 1302 },
                },
              }}
              value={finishTimePart}
              onChange={(value) => {
                updateFinish(finishDatePart, value ?? null)
              }}
              onApply={(value) => {
                updateFinish(finishDatePart, value ?? null)
              }}
              onClear={() => {
                updateFinish(finishDatePart, null)
              }}></TimePicker>
          </div>
        </div>
        <p className="error-message">{showDurationErrMessage ? durationErrMessage : ""}</p>
      </div>
      <TextField
        label={
          <span>
            {t("memo")} <i>({t("optional")})</i>
          </span>
        }
        defaultValue={tempActivity?.memo}
        multiline
        maxRows={5}
        onChange={(e) => setTempActivity({ ...tempActivity, memo: e.target.value } as Activity)}
        slotProps={{
          htmlInput: {
            maxLength: 500,
          },
        }}
      />
      <MapWithComboBox />
    </div>
  )
}
